import {
    Box,
    Step,
    StepConnector,
    stepConnectorClasses,
    StepLabel,
    Stepper,
    styled,
    Typography,
    useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useMemo } from 'react';

import {
    CLASSIC_PIPELINE_STAGES,
    JobApplicationStatus,
    jobApplicationStatusLabels,
    PREMIUM_PIPELINE_STAGES,
    PremiumJobApplicationStatus,
    premiumJobApplicationStatusLabels,
} from '@modules/jobApplications/statuses';
import { getPremiumStatusColors, getStatusColors } from '@modules/jobApplications/utils';

interface ApplicationPipelineStepperProps {
    status?: string | null;
    subStatus?: string | null;
    recruitmentMethod?: string | null;
}

const PipelineConnector = styled(StepConnector)(({ theme }) => ({
    // Positioning only applies in alternativeLabel mode (where MUI absolutely
    // positions the connector). Ignored for the standard horizontal/vertical
    // flex layout, so it no longer leaks into the wrong orientation.
    width: 60,
    flex: 'none',
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 14,
        left: `calc(-50% + ${theme.spacing(2)})`,
        right: `calc(50% + ${theme.spacing(2)})`,
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.border?.main ?? theme.palette.divider,
    },
    [`& .${stepConnectorClasses.lineHorizontal}`]: {
        borderTopWidth: 2,
    },
    [`& .${stepConnectorClasses.lineVertical}`]: {
        borderLeftWidth: 2,
        minHeight: theme.spacing(3),
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.primary.main,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.successGreen?.main ?? theme.palette.success.main,
    },
}));

const ApplicationPipelineStepper: React.FC<ApplicationPipelineStepperProps> = ({
    status,
    subStatus,
    recruitmentMethod,
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const isPremium = recruitmentMethod === 'premium';

    const { stages, labels, isRejected } = useMemo(() => {
        if (isPremium) {
            const colors = getPremiumStatusColors(status ?? undefined, subStatus ?? undefined);
            return {
                stages: PREMIUM_PIPELINE_STAGES as readonly string[],
                labels: premiumJobApplicationStatusLabels,
                isRejected: status === PremiumJobApplicationStatus.Rejected || colors.label === 'Refusé',
            };
        }
        const colors = getStatusColors(status ?? undefined, subStatus ?? undefined);
        return {
            stages: CLASSIC_PIPELINE_STAGES as readonly string[],
            labels: jobApplicationStatusLabels,
            isRejected: status === JobApplicationStatus.Rejected || colors.label === 'Refusé',
        };
    }, [isPremium, status, subStatus]);

    // Active step index. -1 means "unknown / no status yet".
    // For premium applications with no status, default to the first stage (Candidature)
    // so the stepper still communicates where the candidate is.
    const activeStep = useMemo(() => {
        const idx = stages.indexOf(status ?? '');
        if (idx === -1 && isPremium) return 0;
        return idx;
    }, [isPremium, stages, status]);

    const activeColors = isPremium
        ? getPremiumStatusColors(status ?? undefined, subStatus ?? undefined)
        : getStatusColors(status ?? undefined, subStatus ?? undefined);

    const getLabel = (stage: string) => labels[stage as keyof typeof labels] ?? stage;

    return (
        <Box>
            {isRejected ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.errorRed.main,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="subtitle2" sx={{ color: theme.palette.errorRed.main, fontWeight: 700 }}>
                        Refusé{subStatus ? ` — ${getLabel(subStatus)}` : ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}>
                    <Stepper
                        activeStep={activeStep}
                        orientation={'horizontal'}
                        connector={<PipelineConnector />}
                        sx={{
                            '& .Mui-completed .MuiStepIcon-root': { color: theme.palette.successGreen.main },
                            '& .Mui-active .MuiStepIcon-root': { color: activeColors.background },
                            '& .MuiStepLabel-label': { fontSize: '0.8125rem' },
                            '& .MuiStepLabel-labelContainer': { whiteSpace: 'nowrap' },
                        }}>
                        {stages.map((stage, index) => {
                            const isActive = index === activeStep;
                            const isCompleted = activeStep > index;
                            return (
                                <Step key={stage} completed={isCompleted}>
                                    <StepLabel>
                                        <Typography
                                            variant={isActive ? 'subtitle2' : 'body2'}
                                            component="span"
                                            sx={{
                                                fontWeight: isActive ? 700 : 400,
                                                color: isActive
                                                    ? theme.palette.text.primary
                                                    : theme.palette.text.secondary,
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {getLabel(stage)}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Box>
            )}
        </Box>
    );
};

export default ApplicationPipelineStepper;
